import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Search, Loader2, Crosshair } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const NOMINATIM = "https://nominatim.openstreetmap.org";

const pinIcon = L.divIcon({
  className: "",
  html: `<div style="background:#dc2626;width:32px;height:32px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 3px 8px rgba(0,0,0,.35);border:3px solid #fff"></div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 30],
});

export async function reverseGeocode(lat, lng) {
  const r = await fetch(`${NOMINATIM}/reverse?format=json&addressdetails=1&lat=${lat}&lon=${lng}`);
  return r.json();
}

function SearchBox({ onPick }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState([]);

  const run = async (e) => {
    e.preventDefault();
    if (q.trim().length < 3) return toast.error("Type at least 3 letters");
    setBusy(true);
    try {
      const r = await fetch(`${NOMINATIM}/search?format=json&limit=5&countrycodes=in&q=${encodeURIComponent(q.trim())}`);
      const data = await r.json();
      setResults(data);
      if (!data.length) toast.error("No places found — try a nearby landmark");
    } catch {
      toast.error("Search failed, try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative">
      <form onSubmit={run} className="flex gap-2">
        <Input data-testid="map-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search area, street or landmark" />
        <Button type="submit" variant="outline" disabled={busy} data-testid="map-search-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
        </Button>
      </form>
      {results.length > 0 && (
        <ul className="absolute z-[1000] mt-1 w-full rounded-md border border-border bg-card shadow-lg max-h-60 overflow-auto" data-testid="map-search-results">
          {results.map((r, i) => (
            <li key={r.place_id}>
              <button
                type="button"
                data-testid={`map-search-result-${i}`}
                className="w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors"
                onClick={() => { onPick(parseFloat(r.lat), parseFloat(r.lon)); setResults([]); }}
              >
                {r.display_name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function LocationPicker({ lat, lng, onChange }) {
  const elRef = useRef(null);
  const map = useRef(null);
  const marker = useRef(null);
  const cb = useRef(onChange);
  cb.current = onChange;

  const valid = Number.isFinite(lat) && Number.isFinite(lng);

  useEffect(() => {
    const start = valid ? [lat, lng] : [20.5937, 78.9629];
    map.current = L.map(elRef.current, { scrollWheelZoom: false }).setView(start, valid ? 17 : 5);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: "&copy; OpenStreetMap",
    }).addTo(map.current);
    marker.current = L.marker(start, { icon: pinIcon, draggable: true }).addTo(map.current);
    marker.current.on("dragend", () => {
      const p = marker.current.getLatLng();
      cb.current(p.lat, p.lng);
    });
    map.current.on("click", (e) => {
      marker.current.setLatLng(e.latlng);
      cb.current(e.latlng.lat, e.latlng.lng);
    });
    return () => { map.current.remove(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!valid || !marker.current) return;
    const cur = marker.current.getLatLng();
    if (Math.abs(cur.lat - lat) < 1e-7 && Math.abs(cur.lng - lng) < 1e-7) return;
    marker.current.setLatLng([lat, lng]);
    map.current.setView([lat, lng], Math.max(map.current.getZoom(), 16));
  }, [lat, lng, valid]);

  const pick = (a, b) => cb.current(a, b);

  return (
    <div className="space-y-2">
      <SearchBox onPick={pick} />
      <div ref={elRef} data-testid="location-picker-map" className="h-72 w-full rounded-md border border-border overflow-hidden z-0" />
      <p className="text-xs text-muted-foreground flex items-center gap-1.5">
        <Crosshair className="h-3 w-3" /> Drag the red pin or tap the map to set your exact shop entrance.
      </p>
    </div>
  );
}
